/**
 * SystemHealthPage · v2.7.25 (2026-02-21)
 * ---------------------------------------
 * Single-page operational dashboard that pings each critical Admin
 * endpoint and renders a green / amber / red status chip. Used as a
 * post-deploy smoke check — admins can hit /admin/system-health right
 * after pushing to production and instantly see whether each major
 * surface area survived the rollout.
 *
 * Pings:
 *   · /api/health                            (server + Mongo)
 *   · /api/version                           (build / version reporter)
 *   · /api/admin/widget-feed                 (dashboard widgets)
 *   · /api/admin/overview-snapshot           (overview tab)
 *   · /api/admin/stats                       (top-of-page counters)
 *   · /api/admin/earnings/global             (earnings ledger)
 *   · /api/admin/rides/export.zip?count_only=1  (bulk receipts endpoint)
 *   · WS /api/ws/fleet                       (live driver stream)
 *
 * Refreshes on demand via the "Re-run checks" button. Latency is
 * displayed next to each row.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import adminApi from "@/lib/adminApi";
import { ArrowLeft, CheckCircle2, AlertCircle, XCircle, RefreshCw, Loader2, BellRing, BellOff } from "lucide-react";
import SmsOutboxCard from "@/components/admin/SmsOutboxCard";
import SessionRevocationCard from "@/components/admin/SessionRevocationCard";

const PROBES = [
  { id: "health",            label: "Server + MongoDB",        path: "/health",                                             okIfTrue: (d) => d.status === "online" && d.db === true, detail: (d) => `v${d.version}` },
  { id: "widget-feed",       label: "Widget Feed",             path: "/admin/widget-feed",                                  okIfTrue: (d) => !!d.fleet && !!d.dispatch },
  { id: "overview-snapshot", label: "Overview Snapshot",       path: "/admin/overview-snapshot",                            okIfTrue: (d) => !!d.drivers && !!d.riders },
  { id: "stats",             label: "Stats counters",          path: "/admin/stats",                                        okIfTrue: () => true },
  { id: "earnings-global",   label: "Earnings · global",       path: "/admin/earnings/global",                              okIfTrue: () => true },
  { id: "rides-zip",         label: "Bulk Receipts ZIP",       path: "/admin/rides/export.zip?count_only=1",                okIfTrue: () => true, acceptStatuses: [200, 204], headerProbe: "x-bulk-receipt-count" },
];

const STATUS_STYLES = {
  ok:      { icon: CheckCircle2, text: "OK",      cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40" },
  slow:    { icon: AlertCircle,  text: "SLOW",    cls: "bg-amber-400/15 text-amber-300 border-amber-400/40" },
  fail:    { icon: XCircle,      text: "FAIL",    cls: "bg-red-500/15 text-red-300 border-red-400/40" },
  pending: { icon: Loader2,      text: "—",       cls: "bg-zinc-700/30 text-zinc-300 border-zinc-700" },
};

function StatusChip({ status, latencyMs }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <div className="flex items-center gap-2">
      <Badge className={`${s.cls} font-bold uppercase text-[10px] tracking-wider px-2 py-0.5 flex items-center gap-1`}>
        <Icon className={`h-3 w-3 ${status === "pending" ? "animate-spin" : ""}`}/>
        {s.text}
      </Badge>
      {latencyMs != null && (
        <span className="text-[10px] text-zinc-500 tabular-nums">{latencyMs} ms</span>
      )}
    </div>
  );
}

async function probeHttp(probe) {
  const started = performance.now();
  try {
    const tok = localStorage.getItem("rtg_admin_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api${probe.path}`;
    const res = await fetch(url, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      cache: "no-store",
    });
    const ms = Math.round(performance.now() - started);
    const acceptable = probe.acceptStatuses ? probe.acceptStatuses.includes(res.status) : res.ok;
    if (!acceptable) {
      return { status: "fail", latencyMs: ms, detail: `HTTP ${res.status}` };
    }
    // For 204 No Content, surface the count header instead of body
    if (probe.headerProbe) {
      const count = res.headers.get(probe.headerProbe);
      return { status: ms > 1500 ? "slow" : "ok", latencyMs: ms, detail: count != null ? `count=${count}` : "" };
    }
    if (res.status === 204) {
      return { status: ms > 1500 ? "slow" : "ok", latencyMs: ms, detail: "204 no content" };
    }
    const data = await res.json().catch(() => ({}));
    const okSig = probe.okIfTrue ? probe.okIfTrue(data) : true;
    if (!okSig) {
      return { status: "fail", latencyMs: ms, detail: "payload shape mismatch" };
    }
    return {
      status: ms > 1500 ? "slow" : "ok",
      latencyMs: ms,
      detail: probe.detail ? probe.detail(data) : "",
    };
  } catch (e) {
    return { status: "fail", latencyMs: Math.round(performance.now() - started), detail: e?.message || "network error" };
  }
}

function probeWebsocket() {
  return new Promise((resolve) => {
    const started = performance.now();
    try {
      const tok = localStorage.getItem("rtg_admin_token");
      // Strip the protocol from REACT_APP_BACKEND_URL and add /api/ws/fleet
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^https?:\/\//, "");
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const url = `${proto}://${base}/api/ws/dispatch?token=${encodeURIComponent(tok || "")}`;
      const sock = new WebSocket(url);
      const timer = setTimeout(() => {
        sock.close();
        resolve({ status: "fail", latencyMs: Math.round(performance.now() - started), detail: "WS timeout (3s)" });
      }, 3000);
      sock.onopen = () => {
        clearTimeout(timer);
        const ms = Math.round(performance.now() - started);
        sock.close();
        resolve({ status: ms > 1500 ? "slow" : "ok", latencyMs: ms, detail: "connection opened" });
      };
      sock.onerror = () => {
        clearTimeout(timer);
        resolve({ status: "fail", latencyMs: Math.round(performance.now() - started), detail: "WS handshake failed" });
      };
    } catch (e) {
      resolve({ status: "fail", latencyMs: 0, detail: e?.message || "WS init error" });
    }
  });
}

export default function SystemHealthPage() {
  const [results, setResults] = useState(() => {
    const r = {};
    for (const p of PROBES) r[p.id] = { status: "pending" };
    r.ws = { status: "pending" };
    return r;
  });
  const [running, setRunning] = useState(false);
  const [lastRunAt, setLastRunAt] = useState(null);
  // v2.7.26 · Auto-refresh + desktop notifications.
  // `autoRefresh` defaults to ON for wall-monitor use; persists across
  // page reloads via localStorage. `notifyOnFail` controls the
  // Notification API ping that fires the first time a previously-OK
  // probe goes red.
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try { return localStorage.getItem("rtg_health_autorefresh") !== "0"; } catch { return true; }
  });
  const [notifyOnFail, setNotifyOnFail] = useState(() => {
    try { return localStorage.getItem("rtg_health_notify") !== "0"; } catch { return true; }
  });
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported",
  );
  // Snapshot of previous-run statuses so we only ping on the FIRST red.
  const prevStatusesRef = useRef({});

  const runAll = useCallback(async () => {
    setRunning(true);
    // Reset chips
    setResults((prev) => {
      const next = { ...prev };
      for (const p of PROBES) next[p.id] = { status: "pending" };
      next.ws = { status: "pending" };
      return next;
    });
    // Fire all HTTP probes in parallel, plus WS — capture all results
    // locally so we can diff against the previous run BEFORE state
    // settles (state batching would otherwise hide the transitions).
    const fresh = {};
    const httpJobs = PROBES.map(async (p) => {
      const r = await probeHttp(p);
      fresh[p.id] = r;
      setResults((prev) => ({ ...prev, [p.id]: r }));
    });
    const wsJob = probeWebsocket().then((r) => {
      fresh.ws = r;
      setResults((prev) => ({ ...prev, ws: r }));
    });
    await Promise.all([...httpJobs, wsJob]);

    // v2.7.26 · Desktop notification on FIRST red — any probe that was
    // previously OK / slow / unknown but is now `fail`. We only fire
    // once per transition, not on every subsequent refresh while the
    // probe stays red, to avoid notification spam.
    if (notifyOnFail && typeof Notification !== "undefined" && Notification.permission === "granted") {
      const prev = prevStatusesRef.current;
      const newlyFailed = Object.entries(fresh)
        .filter(([id, r]) => r?.status === "fail" && prev[id] !== "fail")
        .map(([id]) => id);
      if (newlyFailed.length > 0) {
        const labels = newlyFailed
          .map((id) => (id === "ws" ? "Live Fleet WebSocket" : PROBES.find((p) => p.id === id)?.label || id))
          .join(", ");
        try {
          const n = new Notification("Buller Taxi · System Health", {
            body: `${newlyFailed.length} probe${newlyFailed.length === 1 ? "" : "s"} just went red: ${labels}`,
            icon: "/favicon.ico",
            tag: "rtg-health-fail",  // collapse repeated alerts
            requireInteraction: false,
          });
          // Auto-close after 8s so the wall monitor stays clean.
          setTimeout(() => { try { n.close(); } catch { /* noop */ } }, 8000);
        } catch { /* Notification creation can throw on some browsers — ignore */ }
      }
    }
    // Stash the latest statuses for the next diff.
    const snapshot = {};
    for (const id of Object.keys(fresh)) snapshot[id] = fresh[id]?.status;
    prevStatusesRef.current = snapshot;

    setLastRunAt(new Date());
    setRunning(false);
  }, [notifyOnFail]);

  useEffect(() => {
    runAll();
  }, [runAll]);

  // v2.7.26 · Auto-refresh loop (60 s). Cleared when `autoRefresh` is
  // toggled off or the page unmounts.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => { runAll(); }, 60_000);
    return () => clearInterval(id);
  }, [autoRefresh, runAll]);

  // Persist the toggle states across reloads (wall monitor friendly).
  useEffect(() => {
    try { localStorage.setItem("rtg_health_autorefresh", autoRefresh ? "1" : "0"); } catch { /* noop */ }
  }, [autoRefresh]);
  useEffect(() => {
    try { localStorage.setItem("rtg_health_notify", notifyOnFail ? "1" : "0"); } catch { /* noop */ }
  }, [notifyOnFail]);

  // Ask for desktop notification permission the first time the user
  // turns the toggle on (and we haven't asked before).
  const requestNotifPerm = useCallback(() => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "granted" || Notification.permission === "denied") {
      setNotifPermission(Notification.permission);
      return;
    }
    Notification.requestPermission().then((perm) => setNotifPermission(perm));
  }, []);
  useEffect(() => {
    if (notifyOnFail) requestNotifPerm();
  }, [notifyOnFail, requestNotifPerm]);

  // Tally overall status
  const allResults = [...PROBES.map((p) => results[p.id]), results.ws];
  const failed = allResults.filter((r) => r?.status === "fail").length;
  const slow = allResults.filter((r) => r?.status === "slow").length;
  const ok = allResults.filter((r) => r?.status === "ok").length;
  const overall = failed > 0 ? "fail" : slow > 0 ? "slow" : ok === allResults.length ? "ok" : "pending";
  const overallStyle = STATUS_STYLES[overall];

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid="system-health-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider">
              <ArrowLeft className="h-4 w-4"/> Admin Console
            </Link>
            <div className="h-6 w-px bg-zinc-800"/>
            <h1 className="text-2xl font-extrabold tracking-tight">System Health</h1>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {/* v2.7.26 · Auto-refresh + desktop notification toggles */}
            <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-refresh-toggle">
              <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh}/>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-300">Auto-refresh · 60 s</span>
            </label>
            <label
              className={`flex items-center gap-2 cursor-pointer ${notifPermission === "denied" ? "opacity-60" : ""}`}
              title={notifPermission === "denied" ? "Browser denied notification permission — re-enable in site settings" : "Desktop notification on first red"}
              data-testid="notify-toggle"
            >
              <Switch checked={notifyOnFail} onCheckedChange={setNotifyOnFail}/>
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-300 flex items-center gap-1">
                {notifyOnFail && notifPermission === "granted" ? <BellRing className="h-3 w-3 text-amber-400"/> : <BellOff className="h-3 w-3"/>}
                Notify on fail
              </span>
            </label>
            <Button
              onClick={runAll}
              disabled={running}
              className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold"
              data-testid="system-health-rerun"
            >
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <RefreshCw className="h-4 w-4 mr-2"/>}
              Re-run checks
            </Button>
          </div>
        </div>

        {/* Overall status */}
        <Card
          className={`p-5 border ${overallStyle.cls.replace("text-", "border-").replace("/15", "/40")} bg-zinc-900/60`}
          data-testid="system-health-overall"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <StatusChip status={overall}/>
              <div>
                <div className="font-bold text-lg">
                  {overall === "ok"   && "All systems operational"}
                  {overall === "slow" && `${slow} system${slow === 1 ? "" : "s"} responding slowly`}
                  {overall === "fail" && `${failed} system${failed === 1 ? "" : "s"} failing`}
                  {overall === "pending" && "Running checks…"}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {ok}/{allResults.length} OK · {slow} slow · {failed} fail
                  {lastRunAt && ` · last checked ${lastRunAt.toLocaleTimeString()}`}
                </div>
              </div>
            </div>
            <Badge className="bg-zinc-950 border-zinc-800 text-zinc-400 text-[10px] uppercase tracking-wider">
              Post-deploy smoke test
            </Badge>
          </div>
        </Card>

        {/* Per-probe results */}
        <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="system-health-probes">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-2 mb-3 border-b border-zinc-700/40">
            Endpoint probes
          </div>
          <div className="space-y-2">
            {PROBES.map((p) => {
              const r = results[p.id] || { status: "pending" };
              return (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-4 px-3 py-2.5 bg-zinc-950 border border-zinc-800 rounded hover:border-zinc-700 transition-colors"
                  data-testid={`probe-${p.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">{p.label}</div>
                    <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{p.path}</div>
                    {r.detail && (
                      <div className="text-[10px] text-zinc-400 mt-1">{r.detail}</div>
                    )}
                  </div>
                  <StatusChip status={r.status} latencyMs={r.latencyMs}/>
                </div>
              );
            })}
            {/* WebSocket row */}
            <div
              className="flex items-center justify-between gap-4 px-3 py-2.5 bg-zinc-950 border border-zinc-800 rounded hover:border-zinc-700 transition-colors"
              data-testid="probe-ws"
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm">Live Fleet WebSocket</div>
                <div className="text-[10px] text-zinc-500 font-mono mt-0.5">ws(s)://…/api/ws/dispatch</div>
                {results.ws?.detail && (
                  <div className="text-[10px] text-zinc-400 mt-1">{results.ws.detail}</div>
                )}
              </div>
              <StatusChip status={results.ws?.status || "pending"} latencyMs={results.ws?.latencyMs}/>
            </div>
          </div>
        </Card>

        <div className="text-[10px] text-zinc-500 text-center">
          Probes run with the current admin token · WS test opens + closes a transient connection ·
          {autoRefresh ? " auto-refreshing every 60 s" : " auto-refresh paused"}
          {notifyOnFail && notifPermission === "granted" && " · desktop alerts on red"}
          {notifyOnFail && notifPermission === "denied" && " · notification permission denied"}
          .
        </div>

        {/* v2.7.65+E70.1 · SMS Outbox live viewer */}
        <SmsOutboxCard/>
        {/* v2.7.65+E73 · Session Revocation console */}
        <SessionRevocationCard/>
      </div>
    </div>
  );
}
